/**
 * agreement-terms-authority.ts — which booklet a signed instrument actually incorporates.
 *
 * PURE MODULE: no fetch, no hooks. `useAgreementTerms` resolves the row; this decides what
 * it is allowed to be resolved AGAINST, and it is a decision rather than a lookup detail.
 *
 * ── THE FROZEN CITATION IS THE AUTHORITY. THE FOREIGN KEY IS A HINT. ──────────
 * fleet-backend is adding `agreement_templates.terms_id`, and `get_agreement_documents` will
 * project `terms_id / terms_form_code / terms_version / terms_sha256` alongside each row. It
 * is tempting to read the booklet straight off those columns — one join, no parsing.
 *
 * It would also be wrong. A foreign key is a row that can be repointed after the fact; the
 * sixteen characters printed in clause 3.1 are sealed under the SIGNER'S OWN HASH, and they
 * are what the signature covers. Where the two disagree the instrument wins, because the
 * instrument is the thing that was signed. So the structured columns may only ever CONFIRM
 * the citation — they may not stand in for it, and they may not override it.
 *
 * That is not a hypothetical shape. A template republished against a new terms version while
 * a document's frozen body still cites the old one produces exactly this: FK says v9, the
 * bytes under the signature say v8. The driver is entitled to see v8, or to see the citation
 * as plain text, and never to see v9 presented as "the Terms this agreement incorporates".
 *
 * ── AND UNTIL THOSE COLUMNS EXIST, THIS CHANGES NOTHING ───────────────────────
 * This app ships BEFORE the backend half. `get_agreement_documents` returns no `terms_*`
 * today, so every hint is absent, every lookup key comes off the citation, and the viewer
 * behaves exactly as the ceremony already does. The hint is read when it appears; nothing
 * waits for it.
 */

import type { TermsCitation } from '@/lib/agreement-terms-citation';

/**
 * The structured columns as `get_agreement_documents` will project them.
 *
 * Every key optional and nullable: this is a row shape from a function that does not return
 * them yet, and a shape that pretends otherwise would make the absent case a type error
 * instead of the ordinary case it is.
 */
export interface TermsHint {
    terms_id?: number | null;
    terms_form_code?: string | null;
    terms_version?: number | null;
    terms_sha256?: string | null;
}

/** What a booklet row has to carry for the check below. `AgreementTermsRow` satisfies it. */
export interface DigestedTermsRow {
    body_sha256?: string | null;
}

/**
 * THE PAGE'S SIXTEEN CHARACTERS DECIDE. Not the lookup key, not the title, not the FK.
 *
 * A row whose digest does not begin with the cited prefix is, by the signed page's own
 * words, a different document — and it is treated as unresolved rather than shown with a
 * warning, because a booklet on screen under the heading "the Terms this agreement
 * incorporates" is the app asserting these are the Terms.
 */
export function termsRowMatchesCitation(
    row: DigestedTermsRow | null | undefined,
    citation: TermsCitation | null | undefined,
): boolean {
    if (!row || !citation) return false;
    const digest = row.body_sha256;
    if (typeof digest !== 'string') return false;
    return digest.startsWith(citation.sha16);
}

/** The lookup a booklet is fetched by: `(form_code, version)`, ALWAYS the citation's. */
export interface TermsLookupKey {
    formCode: string;
    version: number;
}

/**
 * The key to fetch by, or null when the instrument cites no booklet at all.
 *
 * The hint is not an argument here on purpose: there is no input that could make this answer
 * anything but the citation's own values, and a signature that took the hint would be a
 * signature that could be changed by an UPDATE on a template.
 */
export function termsLookupKey(citation: TermsCitation | null | undefined): TermsLookupKey | null {
    if (!citation) return null;
    return { formCode: citation.formCode, version: citation.version };
}

/**
 * Whether the structured hint agrees with the frozen citation.
 *
 * Three ways to disagree and they are all one answer: a different form code, a different
 * version, or a digest that does not begin with the cited sixteen. A hint that is simply
 * ABSENT does not disagree — it says nothing, which is the state of every row served by
 * today's backend.
 */
export function termsHintAgrees(
    hint: TermsHint | null | undefined,
    citation: TermsCitation | null | undefined,
): boolean {
    if (!hint || !citation) return false;

    if (typeof hint.terms_form_code === 'string' && hint.terms_form_code !== citation.formCode) {
        return false;
    }
    if (typeof hint.terms_version === 'number' && hint.terms_version !== citation.version) {
        return false;
    }
    if (typeof hint.terms_sha256 === 'string' && !hint.terms_sha256.startsWith(citation.sha16)) {
        return false;
    }
    // Something has to have been said for the hint to agree with anything.
    return (
        typeof hint.terms_form_code === 'string' ||
        typeof hint.terms_version === 'number' ||
        typeof hint.terms_sha256 === 'string' ||
        typeof hint.terms_id === 'number'
    );
}

/** True when a hint is present and contradicts the bytes under the signature. */
export function termsHintContradicts(
    hint: TermsHint | null | undefined,
    citation: TermsCitation | null | undefined,
): boolean {
    if (!hint || !citation) return false;
    const stated =
        typeof hint.terms_form_code === 'string' ||
        typeof hint.terms_version === 'number' ||
        typeof hint.terms_sha256 === 'string';
    return stated && !termsHintAgrees(hint, citation);
}

/** The tab's own label — "Terms v8" — from whichever half of the pair is trustworthy. */
export function termsTabLabel(citation: TermsCitation | null | undefined): string | null {
    if (!citation) return null;
    return `Terms v${citation.version}`;
}

/** The caption a Documents card carries under its single action. */
export function termsCardCaption(
    formCode: string | null | undefined,
    version: number | null | undefined,
): string | null {
    if (typeof formCode !== 'string' || formCode.trim().length === 0) return null;
    if (typeof version !== 'number' || !Number.isInteger(version) || version <= 0) return null;
    return `Incorporates terms ${formCode} v${version}`;
}
